/**
 * What the Amendments panel says — the version history, the comparison, and the
 * sentence a person reads before adopting.
 *
 * `ProtocolIntelligence.tsx` renders this and decides nothing. Every judgement
 * on this screen is a judgement about WORDING — whether a change with no stated
 * impact prints a blank or a sentence, whether a comparison that could not be
 * saved offers a link, what adoption promises and what it refuses to promise —
 * and wording buried in JSX is wording nothing can test. `web` has no test
 * runner and must not gain one, so the judgements live here, pure, and are
 * exercised from the `functions` package (`cd functions && npm run test:web`).
 *
 * Same pattern, and the same reasoning, as `tmfChecklistView.ts` and
 * `tmfAttachments.ts`. It reuses the latter's date formatting rather than
 * carrying a third copy: an adoption date and a filing date are the same
 * question asked about different records, and the shapes they arrive in
 * (a client-SDK `Timestamp`, a callable's `{_seconds}`, a JSON `{seconds}`, a
 * `Date`, epoch millis) are identical because they come from the same Firestore.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE THREE THINGS THIS SCREEN MUST NOT GET WRONG.
 *
 * 1. THE DOCUMENT KIND IS ASKED, NEVER DETECTED. `compareProtocolsV2` refuses an
 *    unrecognised `documentKind` rather than defaulting to one, because diffing
 *    a full revised protocol against a change-only amendment reports nearly
 *    every section as DELETED — a confident, wrong answer that an experienced
 *    reader would take at face value. So there is no default here either:
 *    `kindSelectionError` returns a sentence until a person has actually chosen,
 *    and the panel cannot send the comparison while it is non-empty.
 *
 * 2. ADOPTION IS NOT INERT, AND IT IS NOT A CASCADE. `adoptAmendment.ts` argues
 *    at length that adoption must not re-analyse the protocol: `analyzeProtocolV2`
 *    rewrites the study's name, arms and Schedule of Events with no undo, and
 *    bundling that into a records act gives a person something they did not
 *    consent to. Its answer to "then adoption feels like it did nothing" is to
 *    SHOW what happened — the new version, the archived one still downloadable,
 *    the trial-folder filing, and the flagged artifacts as a to-do list. That is
 *    what `adoptionConfirmation` and `describeVersion` exist to render, and why
 *    the confirmation has a `willNotHappen` list at all. Re-analysis is a
 *    separate, explicit action carrying `reanalysisWarning`.
 *
 * 3. `comparisonId` CAN BE NULL, AND NULL IS NOT AN ERROR. The save is sandboxed
 *    server-side so a storage failure does not throw away a 300-second model
 *    call. The honest reading of null is "this answer is real and cannot be
 *    reopened" — so `describeComparison` says exactly that instead of offering a
 *    reopen that would go nowhere.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { formatAttachedAt } from './tmfAttachments';

// ── The document kind ───────────────────────────────────────────────────────

/** Mirrors `ProtocolDocumentKind` in `functions/src/protocol/protocolVersions.ts`. */
export type ProtocolDocumentKind = 'full-revised' | 'change-only';

export interface DocumentKindChoice {
  kind: ProtocolDocumentKind;
  /** The heading on the choice card. */
  title: string;
  /** One line, and one line only, explaining what this kind of document IS. */
  oneLine: string;
  /** What the comparison will do with it. The two must not read alike. */
  compareBehaviour: string;
}

/**
 * The two kinds, in the order the panel offers them.
 *
 * The strings are asserted against the server's own type declaration by
 * `protocolAmendments.test.ts`, so a third kind added server-side without a card
 * here fails CI rather than silently becoming unreachable.
 */
export const DOCUMENT_KIND_CHOICES: ReadonlyArray<DocumentKindChoice> = [
  {
    kind: 'full-revised',
    title: 'Full revised protocol',
    oneLine:
      'The whole protocol, reissued with the changes already written into it.',
    compareBehaviour:
      'Its text is compared against the protocol this study currently holds, section by section.',
  },
  {
    kind: 'change-only',
    title: 'Change-only amendment',
    oneLine:
      'A short document stating only the changes, leaving the rest of the protocol where it is.',
    compareBehaviour:
      'It is not compared against anything — the changes it states are read straight off it, because comparing a document that omits most of the protocol would report the omissions as deletions.',
  },
];

/** The question above the two cards. Deliberately a question, not a label. */
export const DOCUMENT_KIND_PROMPT = 'Which kind of document is this?';

/**
 * Why the kind cannot be inferred, in one line, shown beside the choice.
 *
 * It is on screen rather than only in this comment because the person choosing
 * is the one who can tell, and they need to know the choice matters.
 */
export const DOCUMENT_KIND_WHY_ASKED =
  'This is asked rather than detected: reading a change-only amendment as a full protocol reports almost every section as deleted, confidently and wrongly.';

export function isDocumentKind(value: unknown): value is ProtocolDocumentKind {
  return value === 'full-revised' || value === 'change-only';
}

/** The kind in words. An unrecognised value is reported as unstated, never guessed. */
export function documentKindLabel(value: unknown): string {
  const found = DOCUMENT_KIND_CHOICES.find(c => c.kind === value);
  return found ? found.title : 'Not stated';
}

/**
 * Why the panel will not proceed, or `''` when it may.
 *
 * Non-empty is the whole gate: the Compare and Adopt buttons are disabled while
 * it is, so no request can reach a server that would have to refuse it.
 */
export function kindSelectionError(kind: unknown): string {
  if (isDocumentKind(kind)) return '';
  return (
    'Say which kind of document this is before comparing it. ' +
    DOCUMENT_KIND_WHY_ASKED
  );
}

// ── Changes ─────────────────────────────────────────────────────────────────

export interface ProtocolChangeLike {
  section?: string | null;
  changeType?: string | null;
  description?: string | null;
  impact?: string | null;
}

export interface ChangeRow {
  section: string;
  changeType: string;
  description: string;
  impact: string;
  hasImpact: boolean;
  /** The impact, or a sentence saying there is none. Never a blank line. */
  impactNote: string;
}

export interface ChangeSectionGroup {
  section: string;
  changes: ChangeRow[];
}

/** Where a change with no section of its own is filed. Named, never a blank heading. */
export const UNSTATED_SECTION = 'Section not stated';

function present(value: unknown): string {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : '';
}

/**
 * One change, with every field guaranteed to be a string.
 *
 * `changes[]` comes from a model, and a model omits fields. A row rendering
 * `{change.impact}` for an absent impact prints nothing, which reads as "no
 * impact" — indistinguishable from a change whose impact was assessed as none.
 * So the absence is said out loud in `impactNote`, and `hasImpact` lets the row
 * style the two differently.
 */
export function describeChange(change: ProtocolChangeLike | null | undefined): ChangeRow {
  const impact = present(change?.impact);
  const description = present(change?.description);
  return {
    section: present(change?.section),
    changeType: present(change?.changeType),
    description: description || 'This change was reported without a description.',
    impact,
    hasImpact: impact.length > 0,
    impactNote: impact || 'No impact stated.',
  };
}

/**
 * The changes filed under their sections, in first-appearance order.
 *
 * FIRST APPEARANCE rather than alphabetical: the model walks the protocol in
 * document order, so first appearance IS the protocol's order, and sorting the
 * headings alphabetically would put section 10 above section 2.
 *
 * The invariant the test holds this to: every change handed in comes back out,
 * exactly once. A grouping that silently drops rows is worse than no grouping.
 */
export function groupChangesBySection(
  changes: ReadonlyArray<ProtocolChangeLike> | null | undefined,
): ChangeSectionGroup[] {
  const groups: ChangeSectionGroup[] = [];
  const byName = new Map<string, ChangeSectionGroup>();

  for (const raw of changes ?? []) {
    if (!raw) continue;
    const row = describeChange(raw);
    const name = row.section || UNSTATED_SECTION;
    let group = byName.get(name);
    if (!group) {
      group = { section: name, changes: [] };
      byName.set(name, group);
      groups.push(group);
    }
    group.changes.push(row);
  }

  return groups;
}

// ── The comparison ──────────────────────────────────────────────────────────

export interface ComparedVersionLike {
  versionId?: string | null;
  label?: string | null;
  storagePath?: string | null;
}

/** `ProtocolComparisonResult` as the callable hands it back. Every field optional. */
export interface ProtocolComparisonLike {
  changes?: ReadonlyArray<ProtocolChangeLike> | null;
  summary?: string | null;
  affectedArtifacts?: ReadonlyArray<string> | null;
  mode?: string | null;
  comparedAgainst?: ComparedVersionLike | null;
  amendmentFilePath?: string | null;
  seededCurrentVersion?: boolean | null;
  comparisonId?: string | null;
  /**
   * The STORED shape's amendment block.
   *
   * The callable returns `amendmentFilePath`; the Firestore record writes
   * `amendment: { storagePath, documentKind }`. Two shapes for the same fact,
   * and until 2026-08-26 it never mattered because nothing read the stored one
   * back. `comparisonFromStoredRecord` normalises it so the panel sees one
   * shape whichever way the comparison arrived.
   */
  amendment?: { storagePath?: unknown; documentKind?: unknown } | null;
}

/**
 * A saved comparison, in the shape the panel already understands.
 *
 * Fills `amendmentFilePath` from the stored `amendment.storagePath` so a
 * reopened comparison behaves exactly like one that has just been run — the
 * alternative is every consumer having to know which of the two shapes it got.
 */
export function comparisonFromStoredRecord(
  record: ProtocolComparisonLike | null | undefined,
): ProtocolComparisonLike | null {
  if (!record) return null;
  const stored = present(record.amendment?.storagePath);
  // In Firestore the comparison's id IS the document's name, so a stored record
  // carries no `comparisonId` of its own. Without this the panel tells a
  // reopened comparison that it could not be saved.
  const storedId = present((record as { id?: unknown }).id);
  return {
    ...record,
    amendmentFilePath: present(record.amendmentFilePath) || stored || null,
    comparisonId: present(record.comparisonId) || storedId || null,
  };
}

/**
 * The two form values a reopened comparison has to bring back with it.
 *
 * `adoptionBlockedReason` gates Adopt on `amendmentPath` and `kind` — the
 * UPLOAD FORM's state, which a reload empties. Restoring the comparison alone
 * therefore brings back the finding and permanently refuses the one action the
 * finding exists to enable, with "Upload the amendment before adopting it."
 * printed under an amendment that plainly is uploaded.
 *
 * The kind is validated rather than trusted: it is asked and never detected, so
 * an unrecognised value must fall back to asking again rather than be passed on
 * to the server as though a person had chosen it.
 */
export function reopenedFormState(record: ProtocolComparisonLike | null | undefined): {
  amendmentPath: string;
  kind: ProtocolDocumentKind | null;
} {
  const stored = present(record?.amendment?.storagePath) || present(record?.amendmentFilePath);
  const kind = record?.amendment?.documentKind;
  return {
    amendmentPath: stored,
    kind: isDocumentKind(kind) ? kind : null,
  };
}

/**
 * MIRRORS `nextAvailableLabel` in `functions/src/protocol/protocolVersions.ts`.
 *
 * The server owns this rule — it writes the label, inside the transaction,
 * against the labels the study actually holds at that moment. This copy exists
 * only so the confirmation dialog can NAME the caption that will be recorded.
 * "Adopt X as this study's protocol?" naming an X nothing writes is the kind of
 * quiet mismatch this screen is otherwise careful about.
 *
 * `protocolAmendments.test.ts` fails if the server export goes away, so a drift
 * shows up as a failing test rather than as a dialog quietly guessing.
 *
 * As on the server: only a DEFAULTED label passes through here. A label the
 * sponsor typed is shown, and recorded, exactly as they wrote it.
 */
export function nextAvailableLabel(base: string, taken: readonly string[]): string {
  const wanted = String(base ?? '').trim();
  if (!wanted) return wanted;
  const used = new Set(taken.map((t) => String(t ?? '').trim()));
  if (!used.has(wanted)) return wanted;
  for (let n = 2; n <= 1000; n += 1) {
    const candidate = `${wanted} (${n})`;
    if (!used.has(candidate)) return candidate;
  }
  return `${wanted} (${used.size + 1})`;
}

export interface ComparisonView {
  summary: string;
  sections: ChangeSectionGroup[];
  changeCount: number;
  /** e.g. `"3 changes across 2 sections"`, or that there were none. */
  headline: string;
  /** Which question was asked — a diff, or a reading of a change-only document. */
  modeLabel: string;
  affectedArtifacts: string[];
  affectedArtifactsNote: string;
  comparedAgainstLabel: string;
  /** Non-empty only when this call gave the study its version 1. */
  seededNote: string;
  saved: boolean;
  savedNote: string;
  /** The id to reopen by, or null when there is nothing to reopen. */
  reopenId: string | null;
}

function cleanList(values: ReadonlyArray<string> | null | undefined): string[] {
  return (values ?? []).map(v => present(v)).filter(Boolean);
}

function plural(count: number, one: string, many: string): string {
  return count === 1 ? one : many;
}

/** Whether a comparison can be opened again without paying for the model call. */
export function canReopenComparison(id: unknown): boolean {
  return present(id).length > 0;
}

/**
 * What the panel says about whether this comparison survives the tab closing.
 *
 * A null `comparisonId` means the run succeeded and the SAVE did not — see the
 * sandbox at the foot of `compareProtocolsHandler`. Offering a reopen anyway
 * would be a link to nothing; saying nothing at all would let a person close a
 * 300-second answer believing it was kept.
 */
export function comparisonSavedNote(id: unknown): string {
  return canReopenComparison(id)
    ? 'This comparison is saved and can be reopened without running it again.'
    : 'This comparison could not be saved, so it cannot be reopened later. ' +
        'Copy anything you need from this screen before leaving it.';
}

function affectedArtifactsNoteFor(artifacts: string[]): string {
  if (artifacts.length === 0) return 'No artifacts were flagged.';
  const noun = plural(artifacts.length, 'artifact', 'artifacts');
  return `${artifacts.length} ${noun} flagged as possibly affected: ${artifacts.join(', ')}.`;
}

/** Everything the comparison panel renders, from what the callable returned. */
export function describeComparison(
  result: ProtocolComparisonLike | null | undefined,
): ComparisonView {
  const sections = groupChangesBySection(result?.changes);
  const changeCount = sections.reduce((n, s) => n + s.changes.length, 0);
  const artifacts = cleanList(result?.affectedArtifacts);
  const comparedAgainstLabel = present(result?.comparedAgainst?.label);
  const id = present(result?.comparisonId) || null;

  const headline =
    changeCount === 0
      ? 'No changes were reported'
      : `${changeCount} ${plural(changeCount, 'change', 'changes')} across ` +
        `${sections.length} ${plural(sections.length, 'section', 'sections')}`;

  const modeLabel =
    result?.mode === 'change-summary'
      ? 'Read as a statement of changes; no comparison was run'
      : 'Compared against the protocol this study currently holds';

  const seededNote = result?.seededCurrentVersion
    ? 'This study had no recorded protocol history, so the protocol it already ' +
      `held was recorded as version "${comparedAgainstLabel}" and the amendment ` +
      'was read against it.'
    : '';

  return {
    summary: present(result?.summary),
    sections,
    changeCount,
    headline,
    modeLabel,
    affectedArtifacts: artifacts,
    affectedArtifactsNote: affectedArtifactsNoteFor(artifacts),
    comparedAgainstLabel,
    seededNote,
    saved: canReopenComparison(id),
    savedNote: comparisonSavedNote(id),
    reopenId: canReopenComparison(id) ? id : null,
  };
}

/**
 * What a comparison loaded back from Firestore means for the study right now.
 *
 * WHY THIS IS NOT JUST "SHOW IT" (2026-08-26). Until today nothing read the
 * saved comparisons at all, so reloading the page lost the result and the Adopt
 * button with it, and the only way forward was to pay for the model call again.
 * Loading the newest one fixes that — but the newest one may ALREADY have been
 * adopted, and putting "Adopt this amendment" in front of a user for something
 * already done would misdescribe the study, which is the fault being fixed.
 *
 * The discriminator is the FILE. A comparison whose amendment is the study's
 * current version has been adopted; one whose amendment is not, has not. Both
 * records carry the storage path, and the path is what adoption moves.
 *
 * A record with no amendment path is `none` rather than adoptable: an Adopt
 * button with nothing behind it is worse than no button.
 */
export type ReopenedComparisonState = 'none' | 'adoptable' | 'already-adopted';

export function reopenedComparisonState(
  comparison: ProtocolComparisonLike | null | undefined,
  currentVersionPath: string | null | undefined,
): ReopenedComparisonState {
  const amendmentPath =
    present(comparison?.amendment?.storagePath) || present(comparison?.amendmentFilePath);
  if (!amendmentPath) return 'none';
  const current = present(currentVersionPath);
  return current && current === amendmentPath ? 'already-adopted' : 'adoptable';
}

// ── Versions ────────────────────────────────────────────────────────────────

/**
 * A day-precision date for anything Firestore can hand back.
 *
 * Re-exported from `tmfAttachments` rather than reimplemented: an adoption date
 * and a filing date arrive in the same five shapes from the same database, and a
 * second implementation is a second thing to get wrong. Guessing wrong prints
 * nothing, which on an adoption date looks like a version nobody adopted.
 */
export const formatAdoptedAt = formatAttachedAt;

export interface VersionFilingLike {
  documentId?: string | null;
  attachmentId?: string | null;
  status?: string | null;
}

/** A stored `protocolVersions` document, as the client SDK hands it back. */
export interface ProtocolVersionLike {
  id?: string | null;
  label?: string | null;
  storagePath?: string | null;
  documentKind?: string | null;
  adoptedBy?: string | null;
  adoptedAt?: unknown;
  supersededAt?: unknown;
  current?: boolean;
  comparisonId?: string | null;
  affectedArtifacts?: ReadonlyArray<string> | null;
  tmf?: VersionFilingLike | null;
}

export interface VersionDownload {
  available: boolean;
  /** Exactly the arguments `getVersionDownloadUrl` takes, or null. */
  request:
    | { sourceDocFamily: 'essentialDoc'; sourceDocId: string; versionId: string }
    | null;
  /** Why there is no download. Non-empty exactly when `available` is false. */
  unavailableReason: string;
}

/**
 * How to download the file a version records, or why you cannot.
 *
 * The route is the version's TRIAL-FOLDER filing, not its `storagePath`. There
 * is no endpoint that hands out a signed URL for an arbitrary storage path, and
 * there should not be — `getVersionDownloadUrl` resolves an attachment id inside
 * an entry the caller has already named and proved membership on, which is a
 * lookup within an authorized scope rather than a key to the bucket.
 *
 * A version with no filing therefore has no download HERE, and that is stated
 * rather than rendered as a button that fails when pressed. It is a real state:
 * the filing is sandboxed server-side so bookkeeping can never fail an adoption,
 * and a seeded version 1 was never filed by anybody.
 */
export function versionDownload(
  version: ProtocolVersionLike | null | undefined,
): VersionDownload {
  const documentId = present(version?.tmf?.documentId);
  const attachmentId = present(version?.tmf?.attachmentId);

  if (!documentId || !attachmentId) {
    return {
      available: false,
      request: null,
      unavailableReason:
        'This version was not filed in the trial folder, so it cannot be ' +
        'downloaded from this screen. Its file is still recorded and nothing ' +
        'has been deleted.',
    };
  }

  return {
    available: true,
    request: {
      sourceDocFamily: 'essentialDoc',
      sourceDocId: documentId,
      versionId: attachmentId,
    },
    unavailableReason: '',
  };
}

export interface DescribeVersionOptions {
  /** uid → display name, e.g. built from the project's members. */
  people?: Record<string, string>;
}

export interface VersionView {
  id: string;
  label: string;
  kindLabel: string;
  current: boolean;
  statusLabel: string;
  adoptedOn: string;
  adoptedBy: string;
  /** `"Adopted 20 Mar 2026 by Dana Reyes"`, degrading to `"Adopted"`. */
  attribution: string;
  supersededOn: string;
  filedNote: string;
  download: VersionDownload;
  comparisonId: string | null;
  affectedArtifacts: string[];
  /** One line per flagged artifact, naming the version it fell behind. */
  staleNotices: string[];
  /** One line for the lot, or `''` when nothing was flagged. */
  staleHeadline: string;
}

/**
 * One row of the version history.
 *
 * `staleNotices` is where the adoption module's decision becomes visible. It
 * froze `affectedArtifacts` onto the version and deliberately did nothing with
 * them; this turns each into a sentence that names the version it fell behind,
 * so "possibly out of date" has a date attached rather than being a vague
 * warning that ages into noise.
 */
export function describeVersion(
  version: ProtocolVersionLike | null | undefined,
  options?: DescribeVersionOptions,
): VersionView {
  const label = present(version?.label);
  const adoptedOn = formatAdoptedAt(version?.adoptedAt);
  const adoptedByUid = present(version?.adoptedBy);
  const adoptedBy = (options?.people ?? {})[adoptedByUid] ?? adoptedByUid;
  const artifacts = cleanList(version?.affectedArtifacts);
  const current = version?.current === true;

  const attributionParts = ['Adopted'];
  if (adoptedOn) attributionParts.push(adoptedOn);
  if (adoptedBy) attributionParts.push(`by ${adoptedBy}`);

  const staleNotices = artifacts.map(a => `${a} may be out of date since "${label}".`);
  const staleHeadline =
    artifacts.length === 0
      ? ''
      : `${artifacts.length} ${plural(artifacts.length, 'artifact', 'artifacts')} ` +
        `may be out of date since "${label}"`;

  return {
    id: present(version?.id),
    label,
    kindLabel: documentKindLabel(version?.documentKind),
    current,
    statusLabel: current ? 'Current' : 'Superseded',
    adoptedOn,
    adoptedBy,
    attribution: attributionParts.join(' '),
    supersededOn: formatAdoptedAt(version?.supersededAt),
    filedNote: version?.tmf
      ? 'Filed in the trial folder under Signed Protocol and Amendments.'
      : 'Not filed in the trial folder.',
    download: versionDownload(version),
    comparisonId: present(version?.comparisonId) || null,
    affectedArtifacts: artifacts,
    staleNotices,
    staleHeadline,
  };
}

export interface VersionHistoryView {
  current: VersionView | null;
  superseded: VersionView[];
  all: VersionView[];
  /** Non-empty exactly when the study has no recorded version at all. */
  emptyNote: string;
}

/**
 * Millis for ordering, from the same shapes `formatAdoptedAt` reads.
 *
 * Separate from the formatter because the formatter is day-precision by design
 * — the question a history answers is which protocol was live, not at what
 * minute — and two versions adopted on the same day still have an order.
 */
function adoptedMillis(version: ProtocolVersionLike): number {
  const raw = version.adoptedAt as
    | { toDate?: () => Date; seconds?: number; _seconds?: number }
    | number
    | Date
    | null
    | undefined;
  if (raw instanceof Date) return raw.getTime();
  if (typeof raw === 'number') return raw;
  if (raw && typeof raw === 'object') {
    if (typeof raw.toDate === 'function') {
      const d = raw.toDate();
      if (d instanceof Date && Number.isFinite(d.getTime())) return d.getTime();
    }
    if (typeof raw.seconds === 'number') return raw.seconds * 1000;
    if (typeof raw._seconds === 'number') return raw._seconds * 1000;
  }
  return 0;
}

/**
 * The current version and the history beneath it, newest first.
 *
 * Reads the newest `current` rather than asserting there is exactly one:
 * `adoptProtocolVersion` enforces that invariant inside a transaction and a
 * reader's job is to answer the question, not re-litigate it. Same choice, and
 * the same reasoning, as `currentProtocolVersion` server-side and
 * `pickCurrentAttachment` in `tmfAttachments`.
 */
export function describeVersionHistory(
  versions: ReadonlyArray<ProtocolVersionLike> | null | undefined,
  options?: DescribeVersionOptions,
): VersionHistoryView {
  const rows = (versions ?? []).filter(Boolean);
  const ordered = rows
    .slice()
    .sort((a, b) => adoptedMillis(b) - adoptedMillis(a))
    .map(v => describeVersion(v, options));

  const current = ordered.find(v => v.current) ?? null;
  const superseded = ordered.filter(v => v !== current);

  return {
    current,
    superseded,
    all: ordered,
    emptyNote:
      ordered.length === 0
        ? 'This study has no recorded protocol version yet. The first comparison ' +
          'records the protocol it already holds as its first version, and nothing ' +
          'is overwritten when it does.'
        : '',
  };
}

// ── Re-analysis: separate, explicit, and warned about ───────────────────────

export const REANALYSE_ACTION_LABEL = 'Re-analyse the protocol';

/**
 * The warning that travels with the re-analyse action.
 *
 * Adoption does not do this, on purpose (see `adoptAmendment.ts`), so this is
 * the only place a person can ask for it — and the only place they can be told
 * what it costs before they do. `analyzeProtocolV2` overwrites
 * `protocolAnalysis/latest`, the project's name, its arms and its visits with no
 * undo and no history.
 */
export function reanalysisWarning(currentLabel: string | null | undefined): string {
  const label = present(currentLabel) || 'the current version';
  return (
    `Re-analysing reads "${label}" from scratch and replaces this study's name, ` +
    'its study arms and its Schedule of Events with what that document says. ' +
    'There is no undo and no history of what was there before. Everything already ' +
    'generated from the previous analysis stays as it is, so the study will be ' +
    'part new and part old until you regenerate it.'
  );
}

// ── The adoption confirmation ───────────────────────────────────────────────

export interface AdoptionConfirmationInput {
  kind: ProtocolDocumentKind;
  /** The label the new version will carry. */
  label: string;
  /** The version being archived, or null when this is the study's first. */
  previousLabel?: string | null;
  affectedArtifacts?: ReadonlyArray<string> | null;
}

export interface AdoptionConfirmation {
  title: string;
  /** What adoption does. Four statements, all true of every adoption. */
  willHappen: string[];
  /** What it deliberately does NOT do. The same promise for both kinds. */
  willNotHappen: string[];
  confirmLabel: string;
  cancelLabel: string;
}

/**
 * The dialog a person reads before adopting.
 *
 * BOTH LISTS ARE THE POINT. Adoption changes the record, the trial folder and
 * the to-do list, and changes nothing else — and a confirmation showing only the
 * first half would let someone press it expecting their CRFs to follow. The
 * second list is not a disclaimer; it is the feature, argued for at length in
 * `adoptAmendment.ts`, and it is why "it felt like nothing happened" is answered
 * by showing what happened rather than by doing more.
 *
 * The two kinds differ in exactly one line, and they must: a change-only
 * amendment becoming current does not mean the study stops needing the full
 * protocol it amends, and a person adopting one should see that said.
 */
export function adoptionConfirmation(i: AdoptionConfirmationInput): AdoptionConfirmation {
  const label = present(i?.label);
  const previous = present(i?.previousLabel);
  const artifacts = cleanList(i?.affectedArtifacts);

  const becomesCurrent =
    i?.kind === 'change-only'
      ? 'This change-only amendment becomes the version this study runs under. ' +
        'It states only its changes, so ' +
        (previous
          ? `"${previous}" stays part of the record you read alongside it.`
          : 'the protocol it amends stays part of the record you read alongside it.')
      : 'This full revised protocol becomes the version this study runs under.';

  const archives = previous
    ? `"${previous}" is archived. It stays listed here and stays downloadable.`
    : 'This is the first protocol version this study has recorded, so nothing is archived.';

  const flagged =
    artifacts.length === 0
      ? 'The comparison flagged no artifacts.'
      : artifacts.length === 1
        ? '1 artifact flagged by the comparison is marked as possibly out of date. ' +
          'It is not changed for you.'
        : `${artifacts.length} artifacts flagged by the comparison are marked as ` +
          'possibly out of date. They are not changed for you.';

  return {
    title: `Adopt "${label}" as this study's protocol?`,
    willHappen: [
      becomesCurrent,
      archives,
      'The document is filed in the trial folder under Signed Protocol and Amendments.',
      'The change history records that you adopted it, and when.',
    ],
    willNotHappen: [
      'The protocol is not re-analysed. The study name, the study arms and the ' +
        'Schedule of Events stay exactly as they are.',
      'Nothing downstream is regenerated — the CRFs, the data dictionary and the ' +
        'edit checks are left alone.',
      flagged,
    ],
    confirmLabel: 'Adopt this amendment',
    cancelLabel: 'Cancel',
  };
}

// ── Who may adopt ───────────────────────────────────────────────────────────

export interface AdoptionGateInput {
  /** The `approve` privilege on this project. */
  canApprove: boolean;
  /** The uploaded amendment's storage path, or `''` before one exists. */
  amendmentPath: string;
  kind: ProtocolDocumentKind | null;
}

/**
 * Why adoption cannot proceed, or `''` when it can.
 *
 * THE PRIVILEGE IS CHECKED FIRST, and the order matters. A reviewer who holds
 * `approve` but not `upload` and an editor who holds `upload` but not `approve`
 * are both real roles here — that separation is wanted, and it is the reason
 * `adoptAmendmentV2` gates on `approve` while `compareProtocolsV2` gates on
 * `generate`. Telling an editor to "upload the amendment first" when the real
 * answer is "your role cannot adopt" sends them round a loop that ends in the
 * same refusal from the server, so the role is named before anything else.
 */
export function adoptionBlockedReason(i: AdoptionGateInput): string {
  if (!i?.canApprove) {
    return (
      'Adopting an amendment declares which protocol governs this study, so it needs ' +
      'approval rights on this project. Your role does not have them — ask someone ' +
      'who does to adopt it.'
    );
  }
  if (!present(i?.amendmentPath)) {
    return 'Upload the amendment before adopting it.';
  }
  return kindSelectionError(i?.kind);
}
